import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import { CenterFlex, Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ProgressBar from '@/components/Chart/ProgressBar/ProgressBar';
import Transition from '@/components/Common/Transition';
import TransitionPortal from '@/components/Common/TransitionPortal';
import List from '@/components/List/List';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import FeedbackStaticModal from '@/features/Feedback/components/FeedbackStaticModal';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import { useToast } from '@/stores/pure/ToastStore';
import { customScrollbar } from '@/styles/style';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { track } from '@/utils/track/track';
import { stringSort, timeOut } from '@/utils/util';

import S from '../styles';
import type { CreateTopicModalProps } from '../types';
import UpperLevelDisplay from '../UpperLevelDisplay/UpperLevelDisplay';
import AITopicCandidateItem from './AITopicCandidateItem';
import FeedbackBlockPreviewItem from './FeedbackBlockPreviewItem';
import type { CandidateItemT } from './types';

type Props = {
  selectedItems: CandidateItemT[];
  setSelectedItems: Dispatch<SetStateAction<CandidateItemT[]>>;
  onBackClick: () => void;
  onSubmitSuccess: () => void;
  closeModal: () => void;
};

enum GENERATE_STATE {
  WAITING,
  PROGRESSING,
  DONE,
}

const ReviewAiTopic = <T extends CreateTopicTarget>(
  props: CreateTopicModalProps<T> & Props,
) => {
  const { setSelectedItems } = props;
  const addToast = useToast();
  const { modalState, status, close, open, setModalState } = useModalState<{
    feedbackAlias: string;
    blockAlias: string;
  } | null>(null);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentPreview, setCurrentPreview] = useState<{
    index: number;
  } | null>(null);

  const submitReq = useMemo(() => {
    switch (props.target) {
      case 'taxonomy':
        return service.taxonomy.createTopicUnderTaxonomy;
      case 'topic':
        return service.taxonomy.createTopicUnderTopic;
      default:
        throw new Error('Invalid create topic target');
    }
  }, [props.target]);

  const submit = async () => {
    try {
      track(AMP_KEYS.TAXONOMY_CREATE_TOPIC_DONE, {
        type: TOPIC_2_TYPE.AI,
      });
      setSubmitLoading(true);
      await submitReq(
        props.targetAlias,
        props.selectedItems.map((item) => {
          return {
            ...item,
            irrelevant_feedback: item.irrelevant_feedback?.map((block) => {
              return {
                block_id: block.alias,
                score: block.score || 0,
              };
            }),
            relevant_feedback: item.relevant_feedback?.map((block) => {
              return {
                block_id: block.alias,
                score: block.score || 0,
              };
            }),
          };
        }),
      );
      await timeOut(2000);
      props.onSubmitSuccess();
      addToast('Successfully create a topic', 'success');
    } catch (e) {
      addToast('Fail to create topics', 'fail');
    } finally {
      setSubmitLoading(false);
    }
  };

  const hasNotReadyCandidate = useMemo(() => {
    return !!props.selectedItems.find((item) => !item.readyState);
  }, [props.selectedItems]);

  const onPromptChange = useCallback(
    (index: number, prompt: AIRulePromptSet, changed: boolean) => {
      setSelectedItems((prev) => {
        const [newArr, target, targetIndex] = getTarget(prev, index);
        newArr.splice(targetIndex, 1, {
          ...target,
          ai_rule: {
            ...target.ai_rule,
            prompt: prompt.prompt,
            prompt_en: prompt.prompt_en,
          },
          readyState: false,
          promptChanged: changed,
        });
        return newArr;
      });
    },
    [setSelectedItems],
  );

  const onRuleChange = (index: number, rule: AIRule) => {
    setSelectedItems((prev) => {
      const [newArr, target, targetIndex] = getTarget(prev, index);
      newArr.splice(targetIndex, 1, {
        ...target,
        ai_rule: rule,
        readyState: false,
      });
      return newArr;
    });
  };

  const onDeleteClick = (index: number) => {
    setSelectedItems((prev) => {
      const [newArr] = getTarget(prev, index);
      newArr.splice(index, 1);
      return newArr;
    });
  };

  const onTitleChange = (index: number, name: string) => {
    setSelectedItems((prev) => {
      const [newArr, target, targetIndex] = getTarget(prev, index);
      newArr.splice(targetIndex, 1, {
        ...target,
        name,
      });
      return newArr;
    });
    return name;
  };

  const setRelevantList = useCallback(
    (index: number, modifiedList: Topic2RelevantListState) => {
      setSelectedItems((prev) => {
        const [newArr, target, targetIndex] = getTarget(prev, index);
        const newTarget: CandidateItemT = {
          ...target,
          ...modifiedList,
        };
        newArr.splice(targetIndex, 1, newTarget);
        return newArr;
      });
    },
    [setSelectedItems],
  );

  const fetchPreview = useCallback(
    async (index: number, promptSet?: AIRulePromptSet) => {
      try {
        const target = props.selectedItems[index];

        const getFilter = (filter?: StructuredFilter) => {
          if (!filter || Object.keys(filter).length === 0) {
            return undefined;
          }
          return filter;
        };

        const baseParams: Topic2CreateParams<TOPIC_2_TYPE.AI> = {
          name: target.name,
          category: TOPIC_2_TYPE.AI,
          ai_rule: {
            ...target.ai_rule,
            ...promptSet,
            filter: getFilter(target.ai_rule.filter),
          },
        };

        let params:
          | GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'taxonomy'>
          | GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'topic'>;

        if (props.target === 'taxonomy') {
          params = {
            ...baseParams,
            taxonomy_id: props.targetAlias,
          } as GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'taxonomy'>;
        } else {
          params = {
            ...baseParams,
            taxonomy_id: props.taxonomyAlias,
            parent_topic_id: props.targetAlias,
          } as GetTopicPreviewParams<TOPIC_2_TYPE.AI, 'topic'>;
        }

        setSelectedItems((prev) => {
          const [newArr, target, targetIndex] = getTarget(prev, index);
          const newTarget: CandidateItemT = {
            ...target,
            isLoading: true,
          };
          newArr.splice(targetIndex, 1, newTarget);
          return newArr;
        });

        if (target.promptChanged) {
          const translation = await service.taxonomy.translatePrompt(
            params.ai_rule.prompt,
          );

          params.ai_rule = {
            filter: target.ai_rule.filter,
            ...translation,
          };

          target.promptChanged = false;
          setSelectedItems((prev) => {
            const [newArr, target, targetIndex] = getTarget(prev, index);
            const newTarget: CandidateItemT = {
              ...target,
              ai_rule: params.ai_rule,
            };
            newArr.splice(targetIndex, 1, newTarget);
            return newArr;
          });
        }

        const res = await service.taxonomy.fetchTopicPreviewFeedback(params);
        const blockList =
          res.feedbacks
            ?.map(
              (f) =>
                f.blocks?.map((block) => ({
                  ...block,
                  feedbackAlias: f.alias,
                })) || [],
            )
            .flat() || [];

        setSelectedItems((prev) => {
          const [newArr, target, targetIndex] = getTarget(prev, index);
          const newTarget: CandidateItemT = {
            ...target,
            irrelevant_feedback: [],
            relevant_feedback: blockList,
            readyState: true,
            promptChanged: false,
            isLoading: false,
          };
          newArr.splice(targetIndex, 1, newTarget);
          return newArr;
        });
        return true;
      } catch (e) {
        setSelectedItems((prev) => {
          const [newArr, target, targetIndex] = getTarget(prev, index);
          const newTarget: CandidateItemT = {
            ...target,
            isError: true,
            isLoading: false,
          };
          newArr.splice(targetIndex, 1, newTarget);
          return newArr;
        });
        return false;
      }
    },
    [
      props.selectedItems,
      props.target,
      props.targetAlias,
      props.taxonomyAlias,
      setSelectedItems,
    ],
  );

  const currentPreviewItem = useMemo(
    () => (currentPreview ? props.selectedItems[currentPreview.index] : null),
    [currentPreview, props.selectedItems],
  );

  const sortedPreviewList = useMemo(() => {
    return (
      currentPreviewItem?.irrelevant_feedback
        ?.concat(currentPreviewItem.relevant_feedback || [])
        .sort((a, b) => stringSort(a.alias, b.alias)) || []
    );
  }, [
    currentPreviewItem?.irrelevant_feedback,
    currentPreviewItem?.relevant_feedback,
  ]);

  const {
    state: generateProgress,
    setState: setGenerateProgress,
    setValue: setGenerateProgressValue,
  } = useStates<{
    state: GENERATE_STATE;
    progress: Progress;
  }>({
    state: GENERATE_STATE.WAITING,
    progress: {
      total: 0,
      current: 0,
    },
  });

  const fetchPromptAndPreview = useCallback(
    async (item: CandidateItemT, index: number) => {
      // prompt가 없으면 AI로 생성
      let promptSet: AIRulePromptSet | undefined;
      if (!item.ai_rule?.prompt) {
        promptSet = await service.taxonomy.generatePrompt(item.name);
        onPromptChange(index, promptSet, false);
      }

      // preview는 항상 fetch (CSV/수동 모두)
      await fetchPreview(index, promptSet);

      setGenerateProgress((prev) => ({
        ...prev,
        progress: {
          ...prev.progress,
          current: prev.progress.current + 1,
        },
      }));
    },
    [fetchPreview, onPromptChange, setGenerateProgress],
  );

  const prepareTopicReview = useCallback(async () => {
    if (generateProgress.state === GENERATE_STATE.WAITING) {
      setGenerateProgress({
        state: GENERATE_STATE.PROGRESSING,
        progress: {
          total: props.selectedItems.length,
          current: 0,
        },
      });
      let promises: Promise<void>[] = [];
      for (const [index, item] of props.selectedItems.entries()) {
        promises.push(fetchPromptAndPreview(item, index));
      }
      await Promise.all(promises);
      await timeOut(500);
      setGenerateProgressValue('state', GENERATE_STATE.DONE);
    }
  }, [
    fetchPromptAndPreview,
    generateProgress.state,
    props.selectedItems,
    setGenerateProgress,
    setGenerateProgressValue,
  ]);

  useEffect(() => {
    prepareTopicReview();
  }, [prepareTopicReview]);

  return (
    <Style
      style={{
        maxWidth: 1300,
        maxHeight: 'calc(100vh - 48px)',
        display: 'flex',
        gap: 16,
      }}
      status={generateProgress.state === GENERATE_STATE.DONE}
      fallback={
        <S.MainLayout style={{ width: 600, height: 400 }} a="center" j="center">
          <VFlex g={8}>
            <Typo type="label2">
              Preparing topic settings... ({generateProgress.progress.current} /{' '}
              {generateProgress.progress.total})
            </Typo>
            <ProgressBar
              total={generateProgress.progress.total}
              current={generateProgress.progress.current}
              style={{ width: 300 }}
            />
          </VFlex>
        </S.MainLayout>
      }
    >
      <>
        <S.MainLayout style={{ maxWidth: 780, minHeight: 600 }}>
          <VFlex className="header">
            <Heading type="h2">Review prompt</Heading>
            <Typo type="body2" weight="m">
              Review and fine tune your prompts. Leverage keywords/filters for
              optimized results.
              <br />
              Decide your prompts carefully as the prompt cannot be updated once
              creation.
            </Typo>
          </VFlex>
          <Divider />
          <VFlex className="body" g={32}>
            <UpperLevelDisplay
              target={props.target}
              targetTopicAlias={props.targetAlias}
              taxonomyAlias={props.taxonomyAlias}
            />
            <List
              className="gap-6 overflow-unset"
              data={props.selectedItems}
              render={(item, index) => {
                return (
                  <AITopicCandidateItem
                    key={`${item.name}_${index}`}
                    candidateItem={item}
                    index={index}
                    selected={currentPreview?.index === index}
                    fetchPreview={fetchPreview}
                    selectPreview={(index) =>
                      setCurrentPreview({
                        index,
                      })
                    }
                    onPromptChange={onPromptChange}
                    onRuleChange={onRuleChange}
                    onDeleteClick={onDeleteClick}
                    onTitleChange={onTitleChange}
                  />
                );
              }}
            />
          </VFlex>
          <Divider />
          <Flex className="footer" j="space-between" a="center">
            <Typo type="label2" className="text-primary-100">
              Selected {props.selectedItems.length}
            </Typo>
            <Flex j="flex-end" g={12}>
              <Button variant="ghost" sizeType="l" onClick={props.onBackClick}>
                Back
              </Button>
              <Tooltip
                tooltipNode="Make sure all your topics are ready"
                disabled={!hasNotReadyCandidate}
              >
                <div>
                  <Button
                    disabled={submitLoading || hasNotReadyCandidate}
                    variant="primary"
                    sizeType="l"
                    onClick={submit}
                  >
                    Done
                  </Button>
                </div>
              </Tooltip>
            </Flex>
          </Flex>
        </S.MainLayout>
        {currentPreviewItem && (
          <S.PreviewLayout style={{ width: 500 }}>
            <Heading type="h2">{currentPreviewItem.name}</Heading>
            <Space h={40} />
            <VFlex f={1} g={16}>
              <VFlex g={12}>
                <Typo type="label1" weight="m">
                  Preview
                </Typo>
                <Typo type="label3" className="text-gray-700">
                  Selected items will be used to find relevant customer feedback
                </Typo>
              </VFlex>
              <List
                className="gap-2 flex-1"
                data={sortedPreviewList}
                isLoading={currentPreviewItem.isLoading}
                isError={currentPreviewItem.isError}
                placeholder={{
                  node: currentPreviewItem.readyState ? (
                    <CenterFlex style={{ flex: 1 }}>
                      <Typo
                        type="body2"
                        className="text-gray-600"
                        style={{ textAlign: 'center' }}
                      >
                        There may be no associated feedback,
                        <br />
                        or the wrong prompt or keyword is set.
                      </Typo>
                    </CenterFlex>
                  ) : (
                    <CenterFlex style={{ flex: 1 }}>
                      <Typo
                        type="body2"
                        className="text-gray-600"
                        style={{ textAlign: 'center' }}
                      >
                        You must update preview after
                        <br />
                        changing prompt or keyword filter
                      </Typo>
                    </CenterFlex>
                  ),
                }}
                render={(block) => {
                  if (!currentPreview) return null;
                  return (
                    <FeedbackBlockPreviewItem
                      key={block.alias}
                      block={block}
                      ignored={
                        !!currentPreviewItem.irrelevant_feedback?.find(
                          (item) => item.alias === block.alias,
                        )
                      }
                      onClick={(block) => {
                        setModalState({
                          feedbackAlias: block.feedbackAlias,
                          blockAlias: block.alias,
                        });
                        open();
                      }}
                      onIrrelevantClick={(block) => {
                        const newIrrelevantList = [
                          ...(currentPreviewItem.irrelevant_feedback || []),
                        ];
                        const newRelevantList = [
                          ...(currentPreviewItem.relevant_feedback || []),
                        ];
                        newIrrelevantList.upsert(
                          block,
                          (el) => el.alias === block.alias,
                        );
                        newRelevantList.remove(
                          block,
                          (el) => el.alias === block.alias,
                        );
                        setRelevantList(currentPreview.index, {
                          irrelevant_feedback: newIrrelevantList,
                          relevant_feedback: newRelevantList,
                        });
                      }}
                      onRestoreClick={(block) => {
                        const newIrrelevantList = [
                          ...(currentPreviewItem.irrelevant_feedback || []),
                        ];
                        const newRelevantList = [
                          ...(currentPreviewItem.relevant_feedback || []),
                        ];
                        newIrrelevantList.remove(
                          block,
                          (el) => el.alias === block.alias,
                        );
                        newRelevantList.upsert(
                          block,
                          (el) => el.alias === block.alias,
                        );
                        setRelevantList(currentPreview.index, {
                          irrelevant_feedback: newIrrelevantList,
                          relevant_feedback: newRelevantList,
                        });
                      }}
                    />
                  );
                }}
              />
            </VFlex>
            <TransitionPortal
              layerId="syncly-popup-layer"
              status={status}
              background={{
                enable: true,
              }}
              close={close}
              style={{
                top: 48,
                right: 48,
              }}
            >
              {modalState && (
                <FeedbackStaticModal
                  style={{
                    position: 'relative',
                    background: 'none',
                    border: 'none',
                    width: 600,
                  }}
                >
                  <FeedbackStaticModal.Header
                    closeModal={close}
                    feedbackAlias={modalState.feedbackAlias}
                  />

                  <FeedbackStaticModal.Body
                    feedbackAlias={modalState.feedbackAlias}
                    focusedBlock={modalState.blockAlias}
                    style={{
                      maxHeight: '80vh',
                      minHeight: '50vh',
                    }}
                  />
                </FeedbackStaticModal>
              )}
            </TransitionPortal>
          </S.PreviewLayout>
        )}
      </>
    </Style>
  );
};

const getTarget = (
  selectedItems: CandidateItemT[],
  index: number,
): [CandidateItemT[], CandidateItemT, number] => {
  let newArr = [...selectedItems];
  const target = newArr[index];
  if (!target) throw new Error('Target is not exist');
  return [newArr, target, index];
};

const Style = styled(Transition)`
  max-width: 1300px;
  max-height: calc(100vh - 48px);
  .body {
    flex: 1;
    overflow: auto;
    padding: 24px 24px 24px 32px;
    ${customScrollbar};
    scrollbar-gutter: stable;
  }
`;

export default ReviewAiTopic;
